'use client';

import React from 'react';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';

interface PaginationProps {
  total: number;
  perPage: number;
  current: number;
  totalPages: number;
  onPageChange?: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  total,
  perPage,
  current,
  totalPages,
  onPageChange,
  onPerPageChange,
}) => {
  const rows = perPage;
  const safeTotalPages = Math.max(1, totalPages || 1);
  const safeCurrent = Math.min(Math.max(1, current), safeTotalPages);
  const canPage = typeof onPageChange === 'function';

  const startRow = total > 0 ? (safeCurrent - 1) * rows + 1 : 0;
  const endRow = total > 0 ? Math.min(safeCurrent * rows, total) : 0;

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];

    for (let i = 1; i <= Math.min(3, safeTotalPages); i++) {
      pages.push(i);
    }

    if (safeTotalPages > 5 && safeCurrent > 4) {
      pages.push('...');
    }

    if (safeTotalPages > 3) {
      pages.push(safeTotalPages);
    }

    return pages;
  };

  return (
    <div className="mt-2 border-t border-gray-100 p-3">
      <div className="hidden md:flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>Rows per page:</span>
          <select
            value={rows}
            onChange={(e) => onPerPageChange?.(Number(e.target.value))}
            disabled={!onPerPageChange}
            className="border border-gray-200 rounded-lg px-2 py-1 text-sm cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>

        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-500 mr-4">
            Showing {startRow}-{endRow} of {total}
          </span>

          <button
            disabled={!canPage || safeCurrent === 1}
            onClick={() => onPageChange?.(safeCurrent - 1)}
            className="disabled:text-gray-300 text-gray-500 hover:bg-gray-100 rounded-lg w-8 h-8 flex items-center justify-center cursor-pointer"
          >
            <BiChevronLeft size={18} />
          </button>

          {getPageNumbers().map((page, idx) =>
            page === '...' ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange?.(Number(page))}
                className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm cursor-pointer ${
                  safeCurrent === page
                    ? 'bg-[#002266] text-white'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {page}
              </button>
            )
          )}

          <button
            disabled={!canPage || safeCurrent === safeTotalPages}
            onClick={() => onPageChange?.(safeCurrent + 1)}
            className="disabled:text-gray-300 text-gray-500 hover:bg-gray-100 rounded-lg w-8 h-8 flex items-center justify-center cursor-pointer"
          >
            <BiChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="flex md:hidden items-center justify-between gap-3">
        <button
          disabled={!canPage || safeCurrent === 1}
          onClick={() => onPageChange?.(safeCurrent - 1)}
          className="px-3 py-2 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          Prev
        </button>

        <div className="flex items-center gap-2 flex-1 justify-center">
          <span className="bg-[#0f1f4b] text-white text-xs font-bold px-3 py-1.5 rounded-lg">
            {safeCurrent}
          </span>
          <span className="text-xs text-gray-400">/</span>
          <span className="text-xs text-gray-600 font-semibold">{safeTotalPages} pages</span>
        </div>

        <button
          disabled={!canPage || safeCurrent === safeTotalPages}
          onClick={() => onPageChange?.(safeCurrent + 1)}
          className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          Next
        </button>
      </div>
    </div>
  );
};
